import { PropType, computed, defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import useVuelidate from "@vuelidate/core";
import { required, requiredIf } from "@vuelidate/validators";
import moment from "moment";

import type { IEmployeeList, IReassignVisitDetails, ISelectedEmployee } from "../../../vue-interfaces/i-reassign-visit";
import { VisitDetails } from "../../../../models/visit-details";
import {
    IFrequencyListRequestParams,
    IFrequencyListResponse,
    ITaskByPatientRequest,
    ITaskByPatientResponse,
    TaskService
} from "../../../../services/task.service";
import type { ITaskDetailsPayload, INewVisitDetailsResponse } from "../../../vue-interfaces/i-task-details";
import { ToastrService } from "../../../../services/toastr.service";
import { ScheduleVisitHomeHealthModel } from "../../../vue-models/schedule-visit-home-health";
import { IDisciplineTaskHH, IGetDisciplineTaskHHParams } from "../../../vue-interfaces/i-discipline-task-hh";
import { LookupService } from "../../../../services/lookup-service";
import { IApplicationAndAccountQueryParam } from "../../../vue-interfaces/i-get-referral";
import { UserService } from "../../../../services/user-service";
import { IGetUsersByAgency } from "../../../vue-interfaces/i-user";
import { UserManager } from "oidc-client";
import {
    ICurrentEpisodeRequestParams,
    IPatientProfileParams,
    PatientService
} from "../../../../services/patient.service";
import { ISpecialtyAxessCare } from "../../../vue-interfaces/i-visit-details";
import { ITypeaheadOptions } from "../../../vue-interfaces/i-typeahead";
import { IProfile } from "../../../../interfaces/i-patient-service";
import { ApplicationEnum, Discipline } from "../../../../enums/enums";
import { getPatientsProfileResponse } from "../../../../common/utilities/get-patient-profile-responses";

import FormSection from "../../../vue-custom-elements/FormSection/FormSection.vue";
import MultiDatePickerHH from "../../../vue-custom-elements/MultiDatePickerHH/MultiDatePickerHH.vue";
import PostAxxesscare from "../../../vue-dialogs/MultipleVisitAxxesscare/MultipleVisitAxxesscare.vue";
import TypeaheadInput from "../../../vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import SaveNotifyUser from "../../../vue-custom-elements/SaveNotifyUser/SaveNotifyUser.vue";

export enum SaveEnum {
    SaveAndExit = "1",
    SaveAndAddAnother = "2",
    SaveAndNotifyEmployee = "3"
}

export interface IVisitOrders {
    discipline: string;
    frequency: string;
    effectiveDate: string;
}

export default defineComponent({
    components: {
        FormSection,
        MultiDatePickerHH,
        TypeaheadInput,
        SaveNotifyUser
    },

    props: {
        visitDetails: { type: Object as PropType<IReassignVisitDetails>, default: {} },
        preSelectedEmployee: { type: Object as PropType<ISelectedEmployee>, default: null },
        isLoading: { type: Boolean, default: false },
        isSaving: { type: Boolean, default: false },
        cancel: { type: Function, default: null },
        ok: { type: Function, default: null },
        addAnother: { type: Function, default: null }
    },

    setup() {
        return { v$: useVuelidate() };
    },

    validations() {
        return {
            selectedTask: { required },
            newVisitDetails: {
                disciplineTask: { required },
                discipline: { required: requiredIf(() => this.newVisitDetails.postToAxxessCare == true) },
                userId: { required }
            },
            visitDates: {
                required
            }
        };
    },

    data() {
        return {
            employeeList: [] as IEmployeeList[],
            _taskService: null,
            _toastrService: null,
            _userService: null,
            _lookupService: null,
            _userManager: null,
            dialogRef: null,
            dialogReference: this.dialogRef as any,
            _user: null,
            taskDetails: {} as VisitDetails,
            isSavingReason: false as boolean,
            showAssignedToList: false as boolean,
            assignedToName: "",
            newVisitDetails: new ScheduleVisitHomeHealthModel(),
            disciplineTasks: [] as IDisciplineTaskHH[],
            selectedTask: {} as ITypeaheadOptions,
            disciplineTasksList: [] as Array<ITypeaheadOptions>,
            disciplineTask: null as IDisciplineTaskHH,
            usersList: [] as IGetUsersByAgency[],
            tasks: [] as ITaskByPatientResponse[],
            visitDatesList: [],
            SaveEnum: SaveEnum,
            currentUserId: "",
            currentEpisodeDetails: null,
            scheduleEmployee: {} as ITypeaheadOptions,
            patientFrequencyList: [] as IFrequencyListResponse[],
            visitOrders: [] as IVisitOrders[],
            specialties: [] as ISpecialtyAxessCare[],
            visitDates: "",
            postToAxxessCare: false as boolean,
            _patientService: null as PatientService,
            patientDetails: null as IProfile,
            carePeriodStart: "" as string,
            carePeriodEnd: "" as string,
            carePeriod: "N/A" as string
        };
    },

    emits: ["loadingChange", "savingChange"],

    created() {
        this._taskService = Container.instance.get(TaskService);
        this._toastrService = Container.instance.get(ToastrService);
        this._userService = Container.instance.get(UserService);
        this._lookupService = Container.instance.get(LookupService);
        this._patientService = Container.instance.get(PatientService);
        this._userManager = Container.instance.get(UserManager);
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    async mounted() {
        await this.getVisitDetails();
        this.preSelectEmployeeAndStartDate();
        await this.getPatientCarePeriod();
    },

    computed: {
        employeeIsPreSelected() {
            return this.preSelectedEmployee !== null && Object.keys(this.preSelectedEmployee).length > 0;
        }
    },

    watch: {
        visitDateChanged() {
            this.visitDatesChanged();
        },
        scheduleEmployee(newValue: ITypeaheadOptions) {
            this.newVisitDetails.userId = newValue?.value;
        },
        selectedTask(newValue: ITypeaheadOptions) {
            if (!!newValue) {
                const taskDetails = this.disciplineTasks.find((task) => {
                    return task.taskName == newValue.name;
                });
                if (taskDetails) {
                    this.disciplineTask = taskDetails;
                    this.newVisitDetails.customId = this.disciplineTask.customId;
                    this.newVisitDetails.disciplineTask = this.disciplineTask.disciplineTask;
                    this.getSpeciality(taskDetails);
                }
            } else {
                this.disciplineTask = null;
                this.newVisitDetails.customId = null;
                this.newVisitDetails.disciplineTask = null;
            }
        }
    },

    methods: {
        async getVisitDetails() {
            Object.assign(this.visitDetails, {});
            try {
                this.$emit("loadingChange", true);
                const payload: ITaskDetailsPayload = {
                    application: this.visitDetails.Application,
                    agencyId: this.visitDetails.AgencyId,
                    eventId: this.visitDetails.Id,
                    episodeId: this.visitDetails.EpisodeId,
                    patientId: this.visitDetails.PatientId
                };

                const visitDetails = await this._taskService.getHHVisitDetails(payload);
                this.newVisitDetails.agencyId = visitDetails.AgencyId;
                this.newVisitDetails.application = visitDetails.Application;
                this.newVisitDetails.disciplineTask = null;
                this.newVisitDetails.userId = null;

                await this.getDisciplineTasks();
                await this.getUsersList();
                await this.getCurrentEpisode();
                await this.getPatientFrequencyList();
                await this.getPatientTasks();
                this.currentUserId = (
                    await this._userService.getCurrentUserId({ agencyId: this.visitDetails.AgencyId })
                ).userId;
            } catch (error) {
                console.log(error);
            } finally {
                this.$emit("loadingChange", false);
            }
        },

        formatTime(dateTime: Date) {
            return dateTime
                ? new Date(dateTime).toLocaleTimeString("en-us", {
                      hour: "2-digit",
                      minute: "2-digit"
                  })
                : "";
        },

        async assignToMe() {
            this.scheduleEmployee = this.usersList
                .filter((user) => user.id == this.currentUserId)
                .map((item) => ({
                    value: item.id,
                    name: `${item.firstName} ${item.lastName}`
                }))[0];
        },

        async employeeChanged(userId: string) {
            this.newVisitDetails.userId = userId;
        },

        async getSpeciality(discipline: any) {
            try {
                this.specialties = await this._taskService.getAxxessCareSpecialties({
                    agencyId: this.visitDetails.AgencyId,
                    application: this.visitDetails.Application,
                    Discipline: discipline.disciplineTask,
                    disciplineDescription:
                        ApplicationEnum.AgencyCore === this.visitDetails.Application ? discipline.discipline : ""
                });
            } catch {
                this._toastrService.error({
                    title: `Fetching Task Type Failed`,
                    message: `Fetching Task Type Operation Failed, Please Contact Axxess if issue still persists.`
                });
            }
        },

        async getDisciplineTasks() {
            try {
                const payload: IGetDisciplineTaskHHParams = {
                    agencyId: this.visitDetails.AgencyId,
                    application: this.visitDetails.Application
                };
                this.disciplineTasks = await this._lookupService.getDisciplineTasks(payload);
                this.disciplineTasksList = this.disciplineTasks.map((item) => {
                    return {
                        name: item.taskName,
                        value: item.discipline,
                        disciplineTask: item.disciplineTask.toString(),
                        customId: item.customId.toString()
                    } as ITypeaheadOptions;
                });
            } catch (e) {
                this._toastrService.error({
                    title: `Fetching Visit Type Failed`,
                    message: `Fetching Visit Type Operation Failed, Please Contact Axxess if issue still persists.`
                });
            }
        },

        availableTasksFilter(filter: string) {
            if (filter) {
                const filterAvailableTasks = this.disciplineTasksList.filter((task) => {
                    return task.name.toLowerCase().includes(filter.toLowerCase());
                });
                return filterAvailableTasks;
            } else {
                return this.disciplineTasksList;
            }
        },

        changeTaskList(data: any) {
            this.selectedTask = data;
        },

        onTaskTypeChange(discipline: any) {
            this.newVisitDetails.discipline = discipline.discipline;
        },

        async getPatientFrequencyList() {
            try {
                const payload: IFrequencyListRequestParams = {
                    agencyId: this.visitDetails.AgencyId,
                    application: this.visitDetails.Application,
                    patientId: this.visitDetails.Id,
                    episodeId: this.currentEpisodeDetails?.id,
                    episodeStartDate: this.currentEpisodeDetails?.startDate
                };
                this.patientFrequencyList = await this._taskService.getFrequencyList(payload);

                this.patientFrequencyList.map((patientFrequency) => {
                    let visitOrder: IVisitOrders = null;
                    if (patientFrequency.frequencyHHA) {
                        visitOrder = {
                            discipline: "HHA",
                            frequency: patientFrequency.frequencyHHA,
                            effectiveDate: moment(patientFrequency.startDate).format("MM/DD/YYYY")
                        };
                        this.visitOrders.push(visitOrder);
                    }
                    if (patientFrequency.frequencyMSW) {
                        visitOrder = {
                            discipline: "MSW",
                            frequency: patientFrequency.frequencyMSW,
                            effectiveDate: moment(patientFrequency.startDate).format("MM/DD/YYYY")
                        };
                        this.visitOrders.push(visitOrder);
                    }
                    if (patientFrequency.frequencyNursing) {
                        visitOrder = {
                            discipline: "SN",
                            frequency: patientFrequency.frequencyNursing,
                            effectiveDate: moment(patientFrequency.startDate).format("MM/DD/YYYY")
                        };
                        this.visitOrders.push(visitOrder);
                    }
                    if (patientFrequency.frequencyOT) {
                        visitOrder = {
                            discipline: "OT",
                            frequency: patientFrequency.frequencyOT,
                            effectiveDate: moment(patientFrequency.startDate).format("MM/DD/YYYY")
                        };
                        this.visitOrders.push(visitOrder);
                    }
                    if (patientFrequency.frequencyPT) {
                        visitOrder = {
                            discipline: "PT",
                            frequency: patientFrequency.frequencyPT,
                            effectiveDate: moment(patientFrequency.startDate).format("MM/DD/YYYY")
                        };
                        this.visitOrders.push(visitOrder);
                    }
                    if (patientFrequency.frequencyST) {
                        visitOrder = {
                            discipline: "ST",
                            frequency: patientFrequency.frequencyST,
                            effectiveDate: moment(patientFrequency.startDate).format("MM/DD/YYYY")
                        };
                        this.visitOrders.push(visitOrder);
                    }

                    this.visitOrders = this.visitOrders.reduce((acc, curr) => {
                        const existingEntry = acc.find((item) => item.discipline === curr.discipline);
                        if (existingEntry) {
                            existingEntry.frequency += ` \n${curr.frequency}`;
                            existingEntry.effectiveDate += ` \n${curr.effectiveDate}`;
                        } else {
                            acc.push(curr);
                        }
                        return acc;
                    }, []);

                    return this.visitOrders;
                });
            } catch (e) {
                this._toastrService.error({
                    title: `Fetching Frequency List Failed`,
                    message: `Fetching Frequency List Operation Failed, Please Contact Axxess if issue still persists.`
                });
            }
        },

        async getCurrentEpisode() {
            try {
                const payload: ICurrentEpisodeRequestParams = {
                    agencyId: this.visitDetails.AgencyId,
                    application: this.visitDetails.Application,
                    patientId: this.visitDetails.Id
                };
                this.currentEpisodeDetails = await this._patientService.getCurrentEpisode(payload);
                this.newVisitDetails.episodeId = this.currentEpisodeDetails.id;
            } catch (e) {
                this._toastrService.error({
                    title: `Fetching Current Episode Failed`,
                    message: `Fetching Current Episode Operation Failed, Please Contact Axxess if issue still persists.`
                });
            }
        },

        async getUsersList() {
            try {
                const payload: IApplicationAndAccountQueryParam = {
                    agencyId: this.visitDetails.AgencyId,
                    application: this.visitDetails.Application
                };
                this.usersList = await this._userService.getUsersByAgency(payload);
            } catch (e) {
                this._toastrService.error({
                    title: `Fetching Users List Failed`,
                    message: `Fetching Users List Operation Failed, Please Contact Axxess if issue still persists.`
                });
            }
        },

        employeeListFilter(filter: string) {
            if (filter) {
                const filterEmployeeList = this.usersList.filter((employee) => {
                    const fullName = `${employee.firstName} ${employee.lastName}`;
                    return fullName.toLowerCase().includes(filter.toLowerCase());
                });
                return filterEmployeeList?.map((item) => {
                    return {
                        name: `${item.firstName} ${item.lastName}`,
                        value: item.id
                    };
                });
            } else {
                return this.usersList?.map((item) => ({
                    value: item.id,
                    name: `${item.firstName} ${item.lastName}`
                }));
            }
        },

        changescheduleEmployee(data: any) {
            this.scheduleEmployee = data;
        },

        visitDatesChanged() {
            if (this.visitDates) {
                this.newVisitDetails.visitDates = this.visitDates.split(", ");
            } else {
                this.newVisitDetails.visitDates = [];
            }
        },

        async getPatientTasks() {
            try {
                const startDate = moment().startOf("month").format("YYYY-MM-DD");
                const endDate = moment(startDate).add(2, "months").endOf("month").format("YYYY-MM-DD");

                const payload: ITaskByPatientRequest = {
                    patientIds: [this.visitDetails.Id],
                    start: startDate,
                    end: endDate
                };
                this.tasks = await this._taskService.getTasksByPatients(payload);
                this.visitDatesList = this.tasks.map((item: ITaskByPatientResponse) => ({
                    ...item,
                    StartDate: new Date(item.StartDate)
                }));
            } catch (e) {
                console.error(e);
            }
        },

        onPostChange(e: any) {
            const val = e.target.checked;
            this.newVisitDetails.postToAxxessCare = val;
        },

        updateResult(result: any) {
            this.visitDates = result;
        },

        async scheduleVisit(saveType: string) {
            const valid = await this.v$.$validate();
            if (!valid) {
                return;
            }
            if (saveType == SaveEnum.SaveAndNotifyEmployee) {
                if (!this.newVisitDetails.userId) {
                    this._toastrService.error({
                        title: `Error`,
                        message: `Please select employee to notify.`
                    });
                    return;
                }
                this.newVisitDetails.NotifyUser = true;
                this.newVisitDetails.PatientName = `${this.visitDetails.FirstName} ${this.visitDetails.LastName}`;
                this.newVisitDetails.TaskName = this.selectedTask.name;
            }
            if (this.newVisitDetails.postToAxxessCare) {
                this.visitDatesChanged();
                let temp: any = { ...this.newVisitDetails };
                temp.PatientId = this.visitDetails.Id;
                let disciplineTask = this.disciplineTasks.find(
                    (task: any) =>
                        task.customId === this.newVisitDetails.customId &&
                        task.disciplineTask === this.newVisitDetails.disciplineTask
                );
                temp.visitType = disciplineTask.taskName;
                temp.customId = this.newVisitDetails.customId;
                temp.DisciplineTaskId = disciplineTask.disciplineTask;
                temp.discipline = disciplineTask.discipline;
                temp.agencyId = this.visitDetails.AgencyId;
                temp.application = this.visitDetails.Application;
                temp.specialityName = this.specialties.find(
                    (task: any) => task.id === this.newVisitDetails.discipline
                ).name;
                temp.specialityId = this.newVisitDetails.discipline;
                temp.eventId = this.visitDetails.Id;
                temp.episodeId = this.newVisitDetails.episodeId;
                temp.id = this.visitDetails.Id;
                temp.visitType = this.disciplineTasks.find(
                    (task: any) =>
                        task.customId === this.newVisitDetails.customId &&
                        task.disciplineTask === this.newVisitDetails.disciplineTask
                ).taskName;
                temp.userId = this.newVisitDetails.userId;
                let data = {
                    visitDetails: temp,
                    taskDetails: this.taskDetails
                };

                this.dialogRef = await this.$dialog.open(PostAxxesscare, {
                    props: {
                        modal: true,
                        showHeader: false
                    },

                    data: {
                        visitDetails: data.visitDetails,
                        taskDetails: data.taskDetails
                    },

                    onClose: (options) => {
                        if (options.data == "cancel") {
                            return;
                        } else {
                            if (saveType == SaveEnum.SaveAndAddAnother) {
                                this.newVisitDetails.disciplineTask = null;
                                this.disciplineTask = null;
                                this.newVisitDetails.userId = null;
                                this.newVisitDetails.visitDates = [];
                                this.newVisitDetails.discipline = null;
                                this.visitDates = "";
                            }

                            if (saveType == SaveEnum.SaveAndExit || saveType == SaveEnum.SaveAndNotifyEmployee) {
                                this.ok();
                            }
                        }
                    }
                });
            } else {
                if (!!this.newVisitDetails) {
                    try {
                        this.visitDatesChanged();
                        this.$emit("savingChange", true);
                        this.newVisitDetails.patientId = this.visitDetails.Id;
                        this.newVisitDetails.agencyId = this.visitDetails.AgencyId;
                        this.newVisitDetails.application = this.visitDetails.Application;
                        const selectedTask = this.disciplineTasks.find(
                            (task: any) =>
                                task.customId === this.selectedTask.customId &&
                                task.disciplineId === this.selectedTask.disciplineId
                        );

                        if (selectedTask) {
                            this.newVisitDetails.disciplineTask = selectedTask.disciplineTask;
                            this.newVisitDetails.customId = selectedTask.customId;
                            this.newVisitDetails.version = selectedTask.version;
                        }
                        const newVisitDetailsResp: INewVisitDetailsResponse = await this._taskService.scheduleVisitHH(
                            this.newVisitDetails
                        );
                        const isWarning = newVisitDetailsResp.Message.includes("Warning");
                        const respMessage = {
                            title: `New Task Created`,
                            message: newVisitDetailsResp?.Message || `The task has been created`
                        };
                        if (isWarning) {
                            this._toastrService.warning(respMessage);
                        } else {
                            this._toastrService.success(respMessage);
                        }
                        if (saveType == SaveEnum.SaveAndExit || saveType == SaveEnum.SaveAndNotifyEmployee) {
                            this.ok();
                        } else {
                            this.newVisitDetails.disciplineTask = null;
                            this.disciplineTask = null;
                            this.newVisitDetails.userId = null;
                            this.newVisitDetails.visitDates = [];
                            this.visitDates = "";
                        }
                    } catch (e) {
                        this._toastrService.error({
                            title: `Visit Scheduled Failed`,
                            message: `Scheduling Visit Operation Failed, Please Contact Axxess if issue still persists.`
                        });
                    } finally {
                        this.$emit("savingChange", false);
                    }
                }
            }
        },

        async getPatientCarePeriod() {
            try {
                const payload: IPatientProfileParams = {
                    agencyId: this.visitDetails.AgencyId,
                    patientId: this.visitDetails.Id,
                    application: ApplicationEnum.AgencyCore
                };
                const response = await this._patientService.getPatientsProfile(payload);
                getPatientsProfileResponse.call(this, response);
            } catch (e) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while loading the Patient Information. Please try again. If the problem persists, please report it.`
                });
                this.cancel();
            }
        },

        isDateInRange(dateStr: string, startDateStr: string, endDateStr: string) {
            if (!dateStr || !startDateStr || !endDateStr) return false;

            const date = moment(dateStr, "MM/DD/YYYY");
            const start = moment(startDateStr);
            const end = moment(endDateStr);

            return date.isBetween(start, end, undefined, "[]");
        },

        preSelectEmployeeAndStartDate() {
            if (this.employeeIsPreSelected) {
                this.visitDates = this.isDateInRange(
                    this.preSelectedEmployee?.Date,
                    this.currentEpisodeDetails?.startDate,
                    this.currentEpisodeDetails?.endDate
                )
                    ? this.preSelectedEmployee?.Date
                    : "";
                this.changescheduleEmployee({
                    value: this.preSelectedEmployee?.Id,
                    name: this.preSelectedEmployee?.DisplayName
                });
            }
        }
    }
});
